/* eslint-disable max-lines-per-function */
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  OutlinedInput,
} from "@mui/material";
import { IAlert } from "../../interfaces/alert";
import {
  CANCELED,
  ERROR,
  FINAL_DOOR_TO_DOOR_STATUS,
  FINAL_REVERSE_LOGISTICS_STATUS,
  ORDER_CANCEL_REASON_PLACEHOLDER,
  ORDER_REFUND_REASON_PLACEHOLDER,
  OWN_FLEET,
  OWN_FLEET_DOOR_TO_DOOR_STATUSES,
  OWN_FLEET_REVERSE_LOGISTICS_STATUSES,
  OWN_FLEET_REVERSE_LOGISTICS_PICKUP_STATUSES,
  REJECTION_TYPE_BY_STATUS,
  RETURNED_TO_SENDER,
  ERRORS_MESSAGES,
  IN_PROCESS,
  PENDING,
} from "../../utils/constants";
import GenericAlert from "../GenericAlert";
import ModalBase from "./ModalBase";
import { IEditOrderModal } from "../../interfaces/modal";
import { bulkEditOrderStatus } from "../../requests/orderRequests";
import { IOrder, IOrdersFilters, IOrderStatus } from "../../interfaces/order";
import { canRefundOrCancel, getOrderStatus } from "../../utils/orders";
import { UserContext } from "../../contexts/userContext";
import { isCaseInsensitiveEqual } from "../../utils/text";
import translate from "../../utils/translator";
import { isValidStatusTransition } from "../../utils/orderStatus";
import { showInputErrors } from "../../utils/credentials";

const getStatusList = (service: string, isOwnFleet: boolean) => {
  if (isOwnFleet) {
    if (service === "REVERSE_LOGISTICS")
      return OWN_FLEET_REVERSE_LOGISTICS_STATUSES;
    else if (service === "REVERSE_LOGISTICS_PICKUP")
      return OWN_FLEET_REVERSE_LOGISTICS_PICKUP_STATUSES;
    return OWN_FLEET_DOOR_TO_DOOR_STATUSES;
  }
  return service === "REVERSE_LOGISTICS" ||
    service === "REVERSE_LOGISTICS_PICKUP"
    ? FINAL_REVERSE_LOGISTICS_STATUS
    : FINAL_DOOR_TO_DOOR_STATUS;
};

const replaceStatusByAlias = (
  setter: React.Dispatch<React.SetStateAction<IOrderStatus[]>>,
  { service, status }: { service: string; status: string },
  aliases: any,
  isOwnFleet: boolean
) => {
  const statusList: IOrderStatus[] = getStatusList(service, isOwnFleet);
  setter(() =>
    statusList
      .filter((option) =>
        isValidStatusTransition(option, isOwnFleet, service, status)
      )
      .map(({ label, id, ...rest }) => ({
        ...rest,
        id,
        label: aliases[id] || label,
      }))
  );
};

const isEnabledEditTrackingAndCarrier = (
  status: string,
) => [PENDING, IN_PROCESS].includes(status);

const EditOrderModal = ({
  open,
  setOpen,
  selectedOrders,
  setSelectedOrders,
  setFilters,
  selectedMarketplace,
  aliases,
}: IEditOrderModal) => {
  const {
    currentUser: { entity },
    rules: { entitiesAbleToCancel },
  } = useContext(UserContext);
  const [selectedStatus, setSelectedStatus] = useState<{
    translation?: string;
    status?: string;
  }>({ translation: "", status: "" });
  const isSingleOrder = selectedOrders.length === 1;
  const [alertDisable, setAlertDisable] = useState({} as IAlert);
  const [disableButton, setDisableButton] = useState(true);
  const [statusList, setStatusList] = useState<IOrderStatus[]>([]);
  const [canEditTrackingAndCarrier, setCanEditTrackingAndCarrier] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");
  const [errors, setErrors] = useState(
    {} as { tracking: string[]; carrierName: string[] }
  );
  const [tracking, setTracking] = useState<string>(
    isSingleOrder ? selectedOrders[0].orderInfo?.custom_tracking_id || "" : ""
  );
  const [carrierName, setCarrierName] = useState<string>(
    isSingleOrder ? selectedOrders[0].orderInfo?.custom_carrier_name || "" : ""
  );
  const isOwnFleet = selectedOrders.every(({ carrier }) =>
    isCaseInsensitiveEqual(carrier, OWN_FLEET)
  );
  const order = selectedOrders[0] || ({} as IOrder);
  const showReasonInput = [CANCELED, RETURNED_TO_SENDER].includes(
    selectedStatus?.status || ""
  );
  const shouldDisableButton = () =>
    !selectedStatus?.status || (showReasonInput && !reason);
  useEffect(() => {
    setDisableButton(shouldDisableButton());
  }, [selectedStatus, reason]);

  const resetState = () => {
    setDisableButton(false);
    setAlertDisable({} as IAlert);
    setSelectedStatus({ translation: "", status: "" });
    setReason("");
    setTracking("");
    setCarrierName("");
  };
  const handleClose = () => {
    setOpen(false);
    resetState();
    if (isSingleOrder) setSelectedOrders([]);
  };
  const handleChange = (_: any, value: any, changeReason: any) => {
    setAlertDisable({} as IAlert);
    setSelectedStatus(
      changeReason === "clear"
        ? { status: "", translation: "" }
        : {
          status: value?.id,
          translation: getOrderStatus(value.id)?.label,
        }
    );
    setCanEditTrackingAndCarrier(isEnabledEditTrackingAndCarrier(value.id));
  };
  const handleConfirm = async () => {
    setDisableButton(true);
    const props: any = {
      body: {
        status: selectedStatus?.status,
        carrier: order?.carrier,
        custom_tracking_id: tracking,
        custom_carrier_name: carrierName,
      },
      marketplaceId: selectedMarketplace,
      trackingNumbers: selectedOrders.map(({ tracking_id }) => tracking_id),
      ...(showReasonInput && {
        rejection_info: {
          entity,
          reason,
          type: REJECTION_TYPE_BY_STATUS[selectedStatus.status as string],
        },
      }),
    };
    const inputErrors: any = {};
    if (tracking && !carrierName)
      inputErrors.carrierName = [ERRORS_MESSAGES.custom_carrier_name_required];
    if (carrierName && !tracking)
      inputErrors.tracking = [ERRORS_MESSAGES.custom_tracking_required];
    setErrors(inputErrors);
    if (Object.keys(inputErrors).length === 0) {
      try {
        await bulkEditOrderStatus(props);
        setOpen(false);
        resetState();
        setFilters({
          carrier: isOwnFleet ? OWN_FLEET : undefined,
        } as IOrdersFilters);
      } catch (error: any) {
        setAlertDisable({
          typeOfAlert: ERROR,
          message: translate(error),
          showAlert: true,
        });
      }
    }
  };
  useEffect(() => {
    replaceStatusByAlias(
      setStatusList,
      { service: order.service, status: order.status },
      aliases,
      isOwnFleet
    );
  }, [selectedMarketplace, order?.service, order?.status]);

  const reasonPlaceholder =
    selectedStatus.status === CANCELED
      ? ORDER_CANCEL_REASON_PLACEHOLDER
      : ORDER_REFUND_REASON_PLACEHOLDER;

  const filteredOwnFleetStatusList = useMemo(() => {
    return statusList.filter((option) => {
      if (option.id === CANCELED)
        return canRefundOrCancel(entity, entitiesAbleToCancel);
      return option;
    });
  }, [statusList, entity]);

  return (
    <ModalBase open={open} handleClose={handleClose}>
      <Grid container spacing={1} alignItems="center">
        <div className="w-100">
          {alertDisable.showAlert && <GenericAlert alert={alertDisable} />}
        </div>
        <Typography className="f-s-20 m-t-20">
          Editar {isSingleOrder ? "estado" : "estados"}
        </Typography>
        <Grid container className={`m-t-20 ${showReasonInput ? "" : "m-b-40"}`}>
          <Typography className="m-b-10">
            Selecciona el{" "}
            {isSingleOrder ? "estado del envío" : "estado de los envíos"}
          </Typography>
          <Autocomplete
            className="w-100"
            id="status"
            inputValue={selectedStatus?.translation || ""}
            options={filteredOwnFleetStatusList}
            noOptionsText="No se encontró el estado"
            onChange={handleChange}
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" label="Estado" />
            )}
          />
        </Grid>
        {showReasonInput && (
          <Grid container className="m-t-20 m-b-40">
            <TextField
              InputProps={{ className: "br-10" }}
              multiline
              fullWidth
              rows={4}
              placeholder={reasonPlaceholder}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Grid>
        )}
        {isSingleOrder && (
          <Grid container spacing={1}>
            <Grid item xs={6} className="m-b-40">
              <OutlinedInput
                rows={6}
                fullWidth
                size="small"
                error={errors.tracking?.length > 0}
                placeholder={"Numero de seguimiento"}
                value={tracking}
                onChange={(e) => {
                  if (!canEditTrackingAndCarrier) return;
                  setDisableButton(false);
                  setErrors({ tracking: [], carrierName: [] });
                  setAlertDisable({} as IAlert);
                  setTracking(e.target.value);
                }}
                disabled={!canEditTrackingAndCarrier}
              />
              {showInputErrors(errors.tracking)}
            </Grid>

            <Grid item xs={isSingleOrder ? 6 : 12} className="m-b-40">
              <OutlinedInput
                size="small"
                placeholder={"Nombre del operador logistico"}
                error={errors.carrierName?.length > 0}
                fullWidth
                value={carrierName}
                onChange={(e) => {
                  if (!canEditTrackingAndCarrier) return;
                  setErrors({ tracking: [], carrierName: [] });
                  setDisableButton(false);
                  setAlertDisable({} as IAlert);
                  setCarrierName(e.target.value);
                }}
                disabled={!canEditTrackingAndCarrier}
              />
              {showInputErrors(errors.carrierName)}
            </Grid>
          </Grid>
        )}
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button fullWidth variant="outlined" onClick={handleClose}>
              Volver
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              disabled={disableButton}
              onClick={handleConfirm}
            >
              Confirmar
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </ModalBase>
  );
};

export default EditOrderModal;
